// src/utils/waiverStatus.js
//
// DD-WAIVER-0723-BUILD W2 — single source of truth (Rule 12) for the
// waiver-vs-ADD action decision on every player-add surface.
//
// Logic mirrors the FA-search ActionButton (TableColumns.js:56-150) exactly,
// per Bible § 4.5.19 (locked 2026-05-14):
//   - On waivers, period active, viewer's team cut him → cooling off (no action)
//   - On waivers, period active, another team cut him  → claim (waiver flow)
//   - On waivers, period expired                        → normal ADD for everyone
//   - Not on waivers                                    → normal ADD
//
// Canonical paths ONLY: player.league_context?.availability?.* — no flat
// fallbacks, no dual-shape acceptance (Bible Rule 2). Literal defaults
// (|| null) are permitted.
//
// FAIL-CLOSED RULE: a player whose status says 'waivers' but whose
// waiver_expires_at is missing or unparseable is treated as an ACTIVE hold
// (expired=false) — the surface must gate the ADD. Never fail open into a
// plain ADD on malformed expiry data.

/**
 * @param {object} player   canonical player object
 * @param {*}      myTeamId the viewing team's id (commissioner mode: the
 *                          acting team's id). null/undefined → cooling-off
 *                          cannot be established (coolingOff=false).
 * @returns {{ onWaivers: boolean,
 *             expired: boolean,
 *             coolingOff: boolean,
 *             waiverExpiresAt: (string|null),
 *             waiverLabel: (string|null) }}
 */
export const getWaiverActionState = (player, myTeamId = null) => {
  const availability = player?.league_context?.availability;

  const onWaivers =
    availability?.on_waivers === true ||
    availability?.status === 'waivers';

  const waiverExpiresAt = availability?.waiver_expires_at || null;
  const waiverLabel = availability?.waiver_label || null;
  const droppedByTeamId = availability?.dropped_by_team_id || null;

  // Mirrors TableColumns.js:83-85. NaN (malformed timestamp) compares false
  // against Date.now(), so a malformed expiry stays expired=false = ACTIVE
  // hold (fail-closed). The explicit isNaN check documents the invariant.
  let expired = false;
  if (onWaivers && waiverExpiresAt) {
    const expiresMs = new Date(waiverExpiresAt).getTime();
    expired = Number.isNaN(expiresMs) ? false : expiresMs <= Date.now();
  }

  // Mirrors TableColumns.js:86-92: cooling-off only when the viewer's team
  // cut the player AND the waiver period hasn't expired.
  const droppedByMyTeam = Boolean(
    onWaivers && droppedByTeamId && myTeamId && droppedByTeamId === myTeamId
  );
  const coolingOff = onWaivers && !expired && droppedByMyTeam;

  return { onWaivers, expired, coolingOff, waiverExpiresAt, waiverLabel };
};

export default getWaiverActionState;
